/**
 * Вкладки расширенной конфигурации LLM-провайдера (поле config в API).
 */

import type { ReactNode } from "react";
import { HelpCircle } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

function FieldHint({ children }: { children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger type="button" className="inline-flex text-muted-foreground hover:text-foreground">
        <HelpCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="sr-only">Подсказка</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm text-xs leading-snug">{children}</TooltipContent>
    </Tooltip>
  );
}

type ConfigPatch = Record<string, unknown>;

export interface LlmProviderConfigTabsProps {
  config: ConfigPatch;
  /** Слияние в config; поля со значением undefined удаляются. */
  onConfigChange: (patch: ConfigPatch) => void;
  stopText: string;
  onStopTextChange: (v: string) => void;
  extraHeadersText: string;
  onExtraHeadersTextChange: (v: string) => void;
  passthroughText: string;
  onPassthroughTextChange: (v: string) => void;
}

export function LlmProviderConfigTabs({
  config,
  onConfigChange,
  stopText,
  onStopTextChange,
  extraHeadersText,
  onExtraHeadersTextChange,
  passthroughText,
  onPassthroughTextChange,
}: LlmProviderConfigTabsProps) {
  const n = (k: string) => (config[k] as number | undefined);
  const setNum = (k: string, v: string) => {
    if (v === "" || v === "-") {
      onConfigChange({ [k]: undefined });
      return;
    }
    const x = Number(v);
    if (Number.isNaN(x)) return;
    onConfigChange({ [k]: x });
  };

  const setBool = (k: string, c: boolean) => onConfigChange({ [k]: c ? true : undefined });

  return (
    <Tabs defaultValue="inference" className="w-full">
      <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto flex-wrap gap-1">
        <TabsTrigger value="inference" className="text-xs sm:text-sm">
          Параметры модели
        </TabsTrigger>
        <TabsTrigger value="resilience" className="text-xs sm:text-sm">
          Надёжность HTTP
        </TabsTrigger>
        <TabsTrigger value="tools" className="text-xs sm:text-sm">
          Инструменты
        </TabsTrigger>
        <TabsTrigger value="advanced" className="text-xs sm:text-sm">
          Дополнительно
        </TabsTrigger>
      </TabsList>

      <TabsContent value="inference" className="space-y-3 mt-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Управляют генерацией ответа. Используются всеми вызовами chat completions через этого провайдера,
          если в коде агента не переопределены явно.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-temp">temperature</Label>
              <FieldHint>
                Случайность выборки токенов: 0 — почти детерминированно, выше — разнообразнее. Диапазон 0…2.
                Для агентов с инструментами часто 0,1—0,3.
              </FieldHint>
            </div>
            <Input
              id="cfg-temp"
              type="number"
              step={0.05}
              min={0}
              max={2}
              placeholder="по умолчанию 0,7"
              value={n("temperature") ?? ""}
              onChange={(e) => setNum("temperature", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-max">max_tokens</Label>
              <FieldHint>Максимум токенов в ответе. Ограничивает длину и стоимость запроса.</FieldHint>
            </div>
            <Input
              id="cfg-max"
              type="number"
              min={1}
              placeholder="например 1024"
              value={n("max_tokens") ?? ""}
              onChange={(e) => setNum("max_tokens", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-topp">top_p</Label>
              <FieldHint>
                Ядерная выборка: альтернатива temperature. Обычно 0,9—1,0. Не задавайте одновременно сильные
                temperature и top_p без необходимости.
              </FieldHint>
            </div>
            <Input
              id="cfg-topp"
              type="number"
              step={0.05}
              min={0}
              max={1}
              placeholder="1,0"
              value={n("top_p") ?? ""}
              onChange={(e) => setNum("top_p", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-freq">frequency_penalty</Label>
              <FieldHint>Штраф за повторение тех же лексем (−2…2). Положительные значения снижают повторы.</FieldHint>
            </div>
            <Input
              id="cfg-freq"
              type="number"
              step={0.1}
              min={-2}
              max={2}
              value={n("frequency_penalty") ?? ""}
              onChange={(e) => setNum("frequency_penalty", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-pres">presence_penalty</Label>
              <FieldHint>Штраф за повторение тем (−2…2). Увеличивает разнообразие тем.</FieldHint>
            </div>
            <Input
              id="cfg-pres"
              type="number"
              step={0.1}
              min={-2}
              max={2}
              value={n("presence_penalty") ?? ""}
              onChange={(e) => setNum("presence_penalty", e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="cfg-stop">stop — стоп-последовательности</Label>
            <FieldHint>
              По одной строке на строку поля. При появлении такой последовательности генерация останавливается.
            </FieldHint>
          </div>
          <Textarea
            id="cfg-stop"
            className="font-mono text-xs min-h-[72px]"
            placeholder="Например: &lt;END&gt;"
            value={stopText}
            onChange={(e) => onStopTextChange(e.target.value)}
          />
        </div>
      </TabsContent>

      <TabsContent value="resilience" className="space-y-3 mt-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Таймаут и повторы при временных ошибках API (429, 502—504). Снижают риск «зависания» агента при сбоях
          сети или перегрузке провайдера.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-to">timeout_seconds</Label>
              <FieldHint>Максимальное ожидание ответа HTTP (секунды), 1—600. Передаётся в клиент OpenAI SDK.</FieldHint>
            </div>
            <Input
              id="cfg-to"
              type="number"
              min={1}
              max={600}
              placeholder="например 60"
              value={n("timeout_seconds") ?? ""}
              onChange={(e) => setNum("timeout_seconds", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-ret">max_retries</Label>
              <FieldHint>
                Число повторных попыток после ошибок 429 / 502 / 503 / 504. 0 — без повторов.
              </FieldHint>
            </div>
            <Input
              id="cfg-ret"
              type="number"
              min={0}
              max={20}
              placeholder="0"
              value={n("max_retries") ?? ""}
              onChange={(e) => setNum("max_retries", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-rd">retry_delay_seconds</Label>
              <FieldHint>Базовая задержка перед первым повтором (сек). К ней добавляется джиттер.</FieldHint>
            </div>
            <Input
              id="cfg-rd"
              type="number"
              min={0}
              step={0.5}
              value={n("retry_delay_seconds") ?? ""}
              onChange={(e) => setNum("retry_delay_seconds", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cfg-bo">retry_backoff_multiplier</Label>
              <FieldHint>Множитель экспоненциальной задержки между попытками (например 2).</FieldHint>
            </div>
            <Input
              id="cfg-bo"
              type="number"
              min={1}
              max={10}
              step={0.1}
              value={n("retry_backoff_multiplier") ?? ""}
              onChange={(e) => setNum("retry_backoff_multiplier", e.target.value)}
            />
          </div>
        </div>
        <div className="rounded-md border border-dashed p-3 space-y-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Лимиты нагрузки (рантайм в процессе backend)</p>
          <p>
            <strong>max_concurrent_requests</strong>, <strong>rate_limit_rpm</strong> и{" "}
            <strong>rate_limit_tpm</strong> применяются в каждом процессе API (asyncio). При нескольких воркерах
            эффективный потолок умножается на их число; для общего лимитера по кластеру нужен Redis и т.п.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="cfg-mcr">max_concurrent_requests</Label>
              <Input
                id="cfg-mcr"
                type="number"
                min={1}
                value={n("max_concurrent_requests") ?? ""}
                onChange={(e) => setNum("max_concurrent_requests", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cfg-rpm">rate_limit_rpm</Label>
              <Input
                id="cfg-rpm"
                type="number"
                min={1}
                placeholder="запросов/мин"
                value={n("rate_limit_rpm") ?? ""}
                onChange={(e) => setNum("rate_limit_rpm", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cfg-tpm">rate_limit_tpm</Label>
              <Input
                id="cfg-tpm"
                type="number"
                min={1}
                placeholder="токенов/мин"
                value={n("rate_limit_tpm") ?? ""}
                onChange={(e) => setNum("rate_limit_tpm", e.target.value)}
              />
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="tools" className="space-y-3 mt-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Поведение при вызовах с инструментами (function calling). Имеет смысл только если агент передаёт список
          tools в запрос.
        </p>
        <div className="space-y-1 max-w-md">
          <div className="flex items-center gap-1.5">
            <Label>tool_choice</Label>
            <FieldHint>
              auto — модель сама решает; required — должна вызвать инструмент; none — не вызывать. Для обычного чата
              без tools параметр не отправляется.
            </FieldHint>
          </div>
          <Select
            value={(config.tool_choice as string) ?? "__unset__"}
            onValueChange={(v) => {
              if (v === "__unset__") onConfigChange({ tool_choice: undefined });
              else onConfigChange({ tool_choice: v });
            }}
          >
            <SelectTrigger className="z-[250]">
              <SelectValue placeholder="По умолчанию (auto при наличии tools)" />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="__unset__">Не задано (рантайм: auto при tools)</SelectItem>
              <SelectItem value="auto">auto</SelectItem>
              <SelectItem value="required">required</SelectItem>
              <SelectItem value="none">none</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>

      <TabsContent value="advanced" className="space-y-4 mt-3 text-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="cfg-eh">extra_headers (JSON)</Label>
            <FieldHint>
              Дополнительные HTTP-заголовки для каждого запроса к API (например идентификаторы для корпоративного
              прокси). Только валидный JSON-объект.
            </FieldHint>
          </div>
          <Textarea
            id="cfg-eh"
            className="font-mono text-xs min-h-[100px]"
            placeholder={'{\n  "X-Custom": "value"\n}'}
            value={extraHeadersText}
            onChange={(e) => onExtraHeadersTextChange(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="cfg-pass">passthrough_params (JSON)</Label>
            <FieldHint>
              Параметры, которые будут объединены с телом chat.completions (специфичные для провайдера). Не
              подставляйте сюда секреты. Критичные поля (model, messages) задаются кодом и перекрывают конфликты.
            </FieldHint>
          </div>
          <Textarea
            id="cfg-pass"
            className="font-mono text-xs min-h-[100px]"
            placeholder="{}"
            value={passthroughText}
            onChange={(e) => onPassthroughTextChange(e.target.value)}
          />
        </div>
        <div className="space-y-3 rounded-md border p-3">
          <p className="text-xs font-medium text-foreground">Мониторинг и оценка затрат (опционально)</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={!!config.enable_request_logging}
                onCheckedChange={(c) => setBool("enable_request_logging", Boolean(c))}
              />
              <span>enable_request_logging</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={!!config.log_full_message_bodies}
                onCheckedChange={(c) => setBool("log_full_message_bodies", Boolean(c))}
              />
              <span>log_full_message_bodies</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={!!config.enable_tracing}
                onCheckedChange={(c) => setBool("enable_tracing", Boolean(c))}
              />
              <span className="inline-flex items-center gap-1">
                enable_tracing
                <FieldHint>
                  Span OpenTelemetry «llm.chat_completions»: provider_id, модель, счётчики токенов. Тексты сообщений в
                  span не попадают. Без настроенного OTLP-экспортёра накладные расходы минимальны (no-op).
                </FieldHint>
              </span>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Подробные логи могут содержать персональные данные. В закрытом контуре согласуйте режим с политикой ИБ.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Label>pii_redaction_mode</Label>
                <FieldHint>
                  omit — только число сообщений и роли; redact — роли и укороченный preview; full — полные сообщения
                  только вместе с log_full_message_bodies. Устаревшие: mask → redact, metadata → omit.
                </FieldHint>
              </div>
              <Select
                value={(config.pii_redaction_mode as string) ?? "__unset__"}
                onValueChange={(v) => {
                  if (v === "__unset__") onConfigChange({ pii_redaction_mode: undefined });
                  else onConfigChange({ pii_redaction_mode: v });
                }}
              >
                <SelectTrigger className="z-[250]">
                  <SelectValue placeholder="omit (по умолчанию)" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="__unset__">По умолчанию (omit)</SelectItem>
                  <SelectItem value="omit">omit</SelectItem>
                  <SelectItem value="redact">redact</SelectItem>
                  <SelectItem value="full">full</SelectItem>
                  <SelectItem value="mask">mask (устар., как redact)</SelectItem>
                  <SelectItem value="metadata">metadata (устар., как omit)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cfg-sv">schema_version</Label>
              <Input
                id="cfg-sv"
                type="number"
                min={1}
                value={n("schema_version") ?? ""}
                onChange={(e) => setNum("schema_version", e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="cfg-cin">cost_per_1k_input_tokens</Label>
              <Input
                id="cfg-cin"
                type="number"
                min={0}
                step={0.000001}
                placeholder="оценка для отчётов"
                value={n("cost_per_1k_input_tokens") ?? ""}
                onChange={(e) => setNum("cost_per_1k_input_tokens", e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cfg-cout">cost_per_1k_output_tokens</Label>
              <Input
                id="cfg-cout"
                type="number"
                min={0}
                step={0.000001}
                value={n("cost_per_1k_output_tokens") ?? ""}
                onChange={(e) => setNum("cost_per_1k_output_tokens", e.target.value)}
              />
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
